import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT, EnemyType, EnemyGrade, ENEMY_TEXTURES, GRADE_MODIFIERS } from '@/config/GameConfig';
import { TECH, fillTechRect, drawTechRect, createTechButton, showConfirmDialog } from '@/ui/TechUI';
import { createPersistentTooltip, PersistentTooltipHandle } from '@/ui/UIHelpers';
import { SoundManager } from '@/systems/SoundManager';
import {
  getCurve, setCurve, saveCurve, resetCurve,
  mapPower, mapWaves, mapTier,
  composeMap, summarizeWave, wavePowerActual,
  gradesForTier,
  WaveEntry,
} from '@/systems/WaveComposer';
import { enemiesForTier } from '@/config/DifficultyScaling';

const TOTAL_MAPS = 99;

// ── 2×2 Grid Layout ──────────────────────────────────────────────
const MARGIN = 10;
const GAP = 10;
const HEADER_H = 26;
const FOOTER_H = 44;
const HALF_W = Math.floor((CANVAS_WIDTH - 2 * MARGIN - GAP) / 2);
const HALF_H = Math.floor((CANVAS_HEIGHT - HEADER_H - FOOTER_H - GAP) / 2);

// Quadrant origins
const Q1 = { x: MARGIN, y: HEADER_H };                                   // top-left: Graph
const Q2 = { x: MARGIN + HALF_W + GAP, y: HEADER_H };                   // top-right: Sliders
const Q3 = { x: MARGIN, y: HEADER_H + HALF_H + GAP };                   // bottom-left: Map Nav
const Q4 = { x: MARGIN + HALF_W + GAP, y: HEADER_H + HALF_H + GAP };   // bottom-right: Wave Table

const PAD = 8;
const PANEL_TITLE_H = 18;
const SPRITE_SIZE = 28;

// Graph dimensions (inside Q1 panel)
const GRAPH_X = Q1.x + PAD;
const GRAPH_Y = Q1.y + PANEL_TITLE_H + PAD;
const GRAPH_W = HALF_W - 2 * PAD;
const GRAPH_H = HALF_H - PANEL_TITLE_H - 2 * PAD;

// Grade badge colors
const GRADE_COLORS: Record<EnemyGrade, number> = {
  [EnemyGrade.NORMAL]: 0x888888,
  [EnemyGrade.ELITE]:  0xff4444,
  [EnemyGrade.BOSS]:   0x4488ff,
  [EnemyGrade.SUPER]:  0xaa44ff,
  [EnemyGrade.ULTRA]:  0x44ff66,
  [EnemyGrade.GOLD]:   0xffcc44,
};

const GRADE_LABELS: Record<EnemyGrade, string> = {
  [EnemyGrade.NORMAL]: 'N',
  [EnemyGrade.ELITE]:  'E',
  [EnemyGrade.BOSS]:   'B',
  [EnemyGrade.SUPER]:  'S',
  [EnemyGrade.ULTRA]:  'U',
  [EnemyGrade.GOLD]:   'Go',
};

// Tooltip descriptions for each panel's "?" icon
const HELP: Record<string, string> = {
  graph:
    'Power budget curve (pink) and wave count\n' +
    '(blue) across all 99 maps.\n' +
    'Click anywhere on the graph to select a map.',
  sliders:
    'Tune the difficulty progression:\n' +
    '\u2022 Base Power = map 1 power budget\n' +
    '\u2022 Growth Rate = exponential scaling\n' +
    '\u2022 Min/Max Waves = wave count range\n' +
    'Changes update the graph & table live.',
  mapNav:
    'Navigate maps to inspect composition.\n' +
    'Arrows: \u00b11 map, double arrows: \u00b110.\n' +
    'Shows tier, wave count, power budget,\n' +
    'enemy pool and available grades for\n' +
    'the selected map.',
  waves:
    'Visual breakdown of each wave.\n' +
    'Sprites = enemy type, badges = grade:\n' +
    '  E=Elite  B=Boss  S=Super\n' +
    '  U=Ultra  Go=Gold\n' +
    'Right column = power per wave.',
};

export class PowerCurveScene extends Phaser.Scene {
  private selectedMap: number = 1;

  // Containers for each quadrant
  private graphContainer!: Phaser.GameObjects.Container;
  private sliderContainer!: Phaser.GameObjects.Container;
  private mapNavContainer!: Phaser.GameObjects.Container;
  private tableContainer!: Phaser.GameObjects.Container;

  // Shared elements
  private mapLabel!: Phaser.GameObjects.Text;
  private infoLabel!: Phaser.GameObjects.Text;
  private poolLabel!: Phaser.GameObjects.Text;
  private graphGfx!: Phaser.GameObjects.Graphics;
  private graphSelector!: Phaser.GameObjects.Rectangle;
  private sliderTexts: Map<string, Phaser.GameObjects.Text> = new Map();

  // Tooltip
  private tooltipHandle!: PersistentTooltipHandle;

  // Wave table scroll
  private tableScrollY: number = 0;
  private tableContentH: number = 0;
  private tableMaskH: number = 0;
  private tableMask!: Phaser.Display.Masks.GeometryMask;
  private totalLabel!: Phaser.GameObjects.Text;

  // Wave cache
  private cachedWaves: WaveEntry[][] = [];
  private cacheKey: string = '';

  constructor() {
    super({ key: 'PowerCurveScene' });
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#0a0a1e');

    // Scene title
    this.add.text(CANVAS_WIDTH / 2, 5, 'POWER CURVE EDITOR', {
      fontSize: '13px', color: '#ff88ff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 0);

    // Create containers
    this.graphContainer = this.add.container(0, 0);
    this.sliderContainer = this.add.container(0, 0);
    this.mapNavContainer = this.add.container(0, 0);
    this.tableContainer = this.add.container(0, 0);

    // Pre-create cross-referenced elements
    this.mapLabel = this.add.text(0, 0, '', {
      fontSize: '10px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    this.graphSelector = this.add.rectangle(GRAPH_X, GRAPH_Y, 2, GRAPH_H, 0xff88ff, 0.6);

    // Mask for wave table scrolling (clip to Q4 content area)
    const maskShape = this.add.graphics().setVisible(false);
    const maskY = Q4.y + PANEL_TITLE_H;
    this.tableMaskH = HALF_H - PANEL_TITLE_H - 30; // leave room for TOTAL at bottom
    maskShape.fillRect(Q4.x, maskY, HALF_W, this.tableMaskH);
    this.tableMask = new Phaser.Display.Masks.GeometryMask(this, maskShape);
    this.tableContainer.setMask(this.tableMask);

    // Total label (outside mask, always visible at bottom of Q4)
    this.totalLabel = this.add.text(Q4.x + HALF_W - PAD, Q4.y + HALF_H - PAD, '', {
      fontSize: '11px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(1, 1);

    // Scroll with mouse wheel — scene-level listener with Q4 bounds check
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, _gos: Phaser.GameObjects.GameObject[], _dx: number, dy: number) => {
      if (pointer.x >= Q4.x && pointer.x <= Q4.x + HALF_W &&
          pointer.y >= Q4.y && pointer.y <= Q4.y + HALF_H) {
        const maxScroll = Math.max(0, this.tableContentH - this.tableMaskH);
        this.tableScrollY = Phaser.Math.Clamp(this.tableScrollY + dy * 0.5, 0, maxScroll);
        this.tableContainer.setY(-this.tableScrollY);
      }
    });

    // Draw panel backgrounds & build content
    this.drawPanel(Q1, 'POWER CURVE', HELP.graph);
    this.drawPanel(Q2, 'CURVE PARAMETERS', HELP.sliders);
    this.drawPanel(Q3, 'MAP NAVIGATION', HELP.mapNav);
    this.drawPanel(Q4, 'WAVES', HELP.waves);

    this.buildGraph();
    this.buildSliders();
    this.buildMapNav();
    this.rebuildTable();
    this.buildButtons();
    this.tooltipHandle = createPersistentTooltip(this, {
      fontSize: '9px', fontColor: '#ddddee', bgColor: 0x0a0a2e,
      strokeColor: 0xff88ff, strokeAlpha: 0.5, borderRadius: 4,
    });

    // ESC to go back
    this.input.keyboard?.on('keydown-ESC', () => {
      saveCurve();
      this.scene.start('TitleScene');
    });
  }

  // ═══════════════════════════════════════════════════════
  // PANEL DRAWING — border, background, title, help icon
  // ═══════════════════════════════════════════════════════

  private drawPanel(q: { x: number; y: number }, title: string, helpText: string): void {
    const gfx = this.add.graphics();
    fillTechRect(gfx, q.x, q.y, HALF_W, HALF_H, 0x111122, 0.6);
    drawTechRect(gfx, q.x, q.y, HALF_W, HALF_H, 0x333366, 0.5);

    const titleText = this.add.text(q.x + PAD, q.y + 4, title, {
      fontSize: '9px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    });

    // "?" help icon next to title
    this.addHelpIcon(q.x + PAD + titleText.width + 14, q.y + 11, helpText);
  }

  // ═══════════════════════════════════════════════════════
  // TOOLTIP SYSTEM
  // ═══════════════════════════════════════════════════════

  // Tooltip handled by shared UIHelpers.createPersistentTooltip (this.tooltipHandle)

  private addHelpIcon(x: number, y: number, helpText: string): void {
    const circle = this.add.circle(x, y, 7, 0x222244, 0.9)
      .setStrokeStyle(1, 0xff88ff, 0.7)
      .setInteractive({ cursor: 'help' });
    this.add.text(x, y, '?', {
      fontSize: '9px', color: '#ff88ff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);

    circle.on('pointerover', () => this.tooltipHandle.show(x, y, helpText));
    circle.on('pointerout', () => this.tooltipHandle.hide());
  }

  // showTooltip handled by shared UIHelpers.createPersistentTooltip (this.tooltipHandle)

  // ═══════════════════════════════════════════════════════
  // Q1 — POWER CURVE GRAPH (top-left)
  // ═══════════════════════════════════════════════════════

  private buildGraph(): void {
    // Curve graphics (redrawn on param change)
    this.graphGfx = this.add.graphics();
    this.graphContainer.add(this.graphGfx);

    // Selector line
    this.graphContainer.add(this.graphSelector);

    // Click hit zone
    const hitZone = this.add.rectangle(
      GRAPH_X + GRAPH_W / 2, GRAPH_Y + GRAPH_H / 2,
      GRAPH_W, GRAPH_H, 0x000000, 0,
    ).setInteractive({ cursor: 'crosshair' });
    this.graphContainer.add(hitZone);

    const selectMap = (px: number) => {
      const ratio = Phaser.Math.Clamp((px - GRAPH_X) / GRAPH_W, 0, 1);
      this.selectedMap = Math.max(1, Math.min(TOTAL_MAPS, Math.round(1 + ratio * (TOTAL_MAPS - 1))));
      this.updateMapSelector();
      this.rebuildTable();
    };

    hitZone.on('pointerdown', (p: Phaser.Input.Pointer) => selectMap(p.x));
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (p.isDown && hitZone.getBounds().contains(p.x, p.y)) selectMap(p.x);
    });

    // Axis labels
    const axStyle = { fontSize: '7px', color: '#666688', fontFamily: 'monospace' };
    this.graphContainer.add(this.add.text(GRAPH_X + 2, GRAPH_Y + 2, 'Power', axStyle));
    this.graphContainer.add(this.add.text(GRAPH_X + GRAPH_W - 24, GRAPH_Y + GRAPH_H - 12, 'Map', axStyle));

    this.drawGraphCurve();
    this.updateMapSelector();
  }

  private drawGraphCurve(): void {
    const gfx = this.graphGfx;
    gfx.clear();

    const maxPwr = mapPower(TOTAL_MAPS);
    const pad = 10;
    const plotW = GRAPH_W - pad * 2;
    const plotH = GRAPH_H - pad * 2;

    // Grid lines
    gfx.lineStyle(0.5, 0x333355, 0.4);
    for (let i = 0; i <= 4; i++) {
      const gy = GRAPH_Y + pad + (plotH * i / 4);
      gfx.beginPath();
      gfx.moveTo(GRAPH_X + pad, gy);
      gfx.lineTo(GRAPH_X + pad + plotW, gy);
      gfx.strokePath();
    }

    // Power curve (pink)
    gfx.lineStyle(2, 0xff88ff, 1);
    gfx.beginPath();
    for (let m = 1; m <= TOTAL_MAPS; m++) {
      const px = GRAPH_X + pad + ((m - 1) / (TOTAL_MAPS - 1)) * plotW;
      const py = GRAPH_Y + pad + plotH - (mapPower(m) / maxPwr) * plotH;
      m === 1 ? gfx.moveTo(px, py) : gfx.lineTo(px, py);
    }
    gfx.strokePath();

    // Wave count curve (blue)
    const maxW = mapWaves(TOTAL_MAPS);
    gfx.lineStyle(1, 0x44ccff, 0.5);
    gfx.beginPath();
    for (let m = 1; m <= TOTAL_MAPS; m++) {
      const px = GRAPH_X + pad + ((m - 1) / (TOTAL_MAPS - 1)) * plotW;
      const py = GRAPH_Y + pad + plotH - (mapWaves(m) / maxW) * plotH;
      m === 1 ? gfx.moveTo(px, py) : gfx.lineTo(px, py);
    }
    gfx.strokePath();

    // Legend
    const lx = GRAPH_X + GRAPH_W - 70;
    gfx.fillStyle(0xff88ff, 1);
    gfx.fillRect(lx, GRAPH_Y + 6, 8, 2);
    gfx.fillStyle(0x44ccff, 0.5);
    gfx.fillRect(lx, GRAPH_Y + 16, 8, 2);

    // Remove old legend texts
    this.graphContainer.getAll().forEach(c => {
      if (c instanceof Phaser.GameObjects.Text && c.getData('legend')) c.destroy();
    });
    const ls = { fontSize: '7px', color: '#aaaacc', fontFamily: 'monospace' };
    const l1 = this.add.text(lx + 12, GRAPH_Y + 3, 'Power', ls).setData('legend', true);
    const l2 = this.add.text(lx + 12, GRAPH_Y + 13, 'Waves', ls).setData('legend', true);
    this.graphContainer.add([l1, l2]);
  }

  private updateMapSelector(): void {
    const px = GRAPH_X + ((this.selectedMap - 1) / (TOTAL_MAPS - 1)) * GRAPH_W;
    this.graphSelector.setX(px);
    this.mapLabel.setText(
      `MAP ${this.selectedMap}  |  Tier ${mapTier(this.selectedMap)}  |  ` +
      `${mapWaves(this.selectedMap)}W  |  Power: ${mapPower(this.selectedMap)}`
    );
    this.poolLabel?.setText(this.getPoolText());
  }

  private getPoolText(): string {
    const tier = mapTier(this.selectedMap);
    const pool = enemiesForTier(tier).map(t => t.substring(0, 3).toUpperCase()).join(' ');
    const grades = gradesForTier(tier).map(g => GRADE_LABELS[g]).join(' ');
    return `Pool: ${pool}    Grades: ${grades}`;
  }

  // ═══════════════════════════════════════════════════════
  // Q2 — CURVE PARAMETERS / SLIDERS (top-right)
  // ═══════════════════════════════════════════════════════

  private buildSliders(): void {
    const baseX = Q2.x + PAD;
    let y = Q2.y + PANEL_TITLE_H + PAD;
    const curve = getCurve();

    const sliders = [
      { key: 'basePower', label: 'Base Power', value: curve.basePower, min: 10, max: 100, step: 5 },
      { key: 'growthRate', label: 'Growth Rate', value: curve.growthRate, min: 1.01, max: 1.15, step: 0.005 },
      { key: 'waveMin', label: 'Min Waves', value: curve.waveMin, min: 3, max: 10, step: 1 },
      { key: 'waveMax', label: 'Max Waves', value: curve.waveMax, min: 10, max: 30, step: 1 },
    ];

    for (const s of sliders) {
      y = this.addSliderRow(baseX, y, s.key, s.label, s.value, s.min, s.max, s.step);
    }

    // Power summary
    y += 12;
    this.infoLabel = this.add.text(baseX, y, '', {
      fontSize: '9px', color: '#aaaacc', fontFamily: 'monospace',
      wordWrap: { width: HALF_W - 2 * PAD },
    });
    this.sliderContainer.add(this.infoLabel);
    this.updateInfoLabel();
  }

  private addSliderRow(
    x: number, y: number,
    key: string, label: string,
    value: number, min: number, max: number, step: number,
  ): number {
    const trackW = HALF_W - 2 * PAD - 60;
    const isFloat = step < 1;

    const lbl = this.add.text(x, y, label, {
      fontSize: '9px', color: '#aaaacc', fontFamily: 'monospace',
    });
    this.sliderContainer.add(lbl);

    const trackY = y + 14;

    // Track
    const bg = this.add.rectangle(x + trackW / 2, trackY + 1, trackW, 3, 0x333355);
    this.sliderContainer.add(bg);

    // Value text
    const valText = this.add.text(x + trackW + 8, trackY - 4,
      isFloat ? value.toFixed(3) : String(value),
      { fontSize: '9px', color: '#ffffff', fontFamily: 'monospace' },
    );
    this.sliderContainer.add(valText);
    this.sliderTexts.set(key, valText);

    // Knob
    const knobX = x + ((value - min) / (max - min)) * trackW;
    const knob = this.add.rectangle(knobX, trackY + 1, 8, 12, 0x1a1a2e).setStrokeStyle(1.5, 0xff88ff);
    this.sliderContainer.add(knob);

    // Hit zone
    const hit = this.add.rectangle(x + trackW / 2, trackY + 1, trackW, 16, 0x000000, 0)
      .setInteractive({ cursor: 'pointer' });
    this.sliderContainer.add(hit);

    const apply = (px: number) => {
      const ratio = Phaser.Math.Clamp((px - x) / trackW, 0, 1);
      let raw = min + ratio * (max - min);
      raw = Math.round(raw / step) * step;
      raw = Phaser.Math.Clamp(raw, min, max);
      knob.setX(x + ((raw - min) / (max - min)) * trackW);
      valText.setText(isFloat ? raw.toFixed(3) : String(raw));
      setCurve({ [key]: raw });
      this.onCurveChanged();
    };

    hit.on('pointerdown', (p: Phaser.Input.Pointer) => apply(p.x));
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (p.isDown && hit.getBounds().contains(p.x, p.y)) apply(p.x);
    });

    return trackY + 22;
  }

  private onCurveChanged(): void {
    this.drawGraphCurve();
    this.updateInfoLabel();
    this.rebuildTable();
  }

  private updateInfoLabel(): void {
    this.infoLabel.setText(
      `Map  1: ${mapPower(1)} pwr, ${mapWaves(1)}W\n` +
      `Map 50: ${mapPower(50)} pwr, ${mapWaves(50)}W\n` +
      `Map 99: ${mapPower(99)} pwr, ${mapWaves(99)}W`
    );
  }

  // ═══════════════════════════════════════════════════════
  // Q3 — MAP NAVIGATION (bottom-left)
  // ═══════════════════════════════════════════════════════

  private buildMapNav(): void {
    const cx = Q3.x + HALF_W / 2;
    const y = Q3.y + PANEL_TITLE_H + PAD + 4;

    // Map label — centered
    this.mapLabel.setPosition(cx, y);

    // Navigation arrows
    const nav = (delta: number) => {
      this.selectedMap = Phaser.Math.Clamp(this.selectedMap + delta, 1, TOTAL_MAPS);
      this.updateMapSelector();
      this.rebuildTable();
    };

    const aStyle = { fontSize: '16px', color: '#ff88ff', fontFamily: 'monospace' };
    const fStyle = { fontSize: '11px', color: '#aa66aa', fontFamily: 'monospace' };
    const x0 = Q3.x + PAD;
    const x1 = Q3.x + HALF_W - PAD;
    const arrowY = y - 2;

    this.add.text(x0, arrowY, '\u25C4\u25C4', fStyle)
      .setInteractive({ cursor: 'pointer' }).on('pointerdown', () => nav(-10));
    this.add.text(x0 + 24, arrowY, '\u25C4', aStyle)
      .setInteractive({ cursor: 'pointer' }).on('pointerdown', () => nav(-1));
    this.add.text(x1 - 14, arrowY, '\u25BA', aStyle).setOrigin(1, 0)
      .setInteractive({ cursor: 'pointer' }).on('pointerdown', () => nav(1));
    this.add.text(x1, arrowY, '\u25BA\u25BA', fStyle).setOrigin(1, 0)
      .setInteractive({ cursor: 'pointer' }).on('pointerdown', () => nav(10));

    // Pool & grades info
    this.poolLabel = this.add.text(Q3.x + PAD, y + 22, '', {
      fontSize: '9px', color: '#888899', fontFamily: 'monospace',
    });

    this.updateMapSelector();
  }

  // ═══════════════════════════════════════════════════════
  // Q4 — WAVE TABLE (bottom-right)
  // ═══════════════════════════════════════════════════════

  private rebuildTable(): void {
    this.tableContainer.removeAll(true);
    this.tableScrollY = 0;
    this.tableContainer.setY(0);

    const m = this.selectedMap;
    const key = `${m}_${JSON.stringify(getCurve())}`;
    if (key !== this.cacheKey) {
      this.cachedWaves = composeMap(m);
      this.cacheKey = key;
    }
    const waves = this.cachedWaves;

    const x = Q4.x + PAD;
    const rightEdge = Q4.x + HALF_W - PAD;
    let y = Q4.y + PANEL_TITLE_H + PAD;
    const rowH = 34;

    // Wave count in header (outside mask — added directly to scene)
    // We use the panel title area which is above the mask
    const waveCountLabel = this.add.text(Q4.x + PAD + 50, Q4.y + 4, `(${waves.length})`, {
      fontSize: '10px', color: '#aaaacc', fontFamily: 'monospace',
    });
    this.tableContainer.add(waveCountLabel);

    // All wave rows (no limit — scrollable)
    for (let w = 0; w < waves.length; w++) {
      const wave = waves[w];
      const summary = summarizeWave(wave);
      const actualPwr = Math.round(wavePowerActual(wave));

      // Wave label
      const wLabel = this.add.text(x, y + rowH / 2, `W${w + 1}`, {
        fontSize: '11px', color: '#888899', fontFamily: 'monospace',
      }).setOrigin(0, 0.5);
      this.tableContainer.add(wLabel);

      // Enemy sprites with counts
      let sx = x + 32;
      for (const entry of summary) {
        if (sx + SPRITE_SIZE + 24 > rightEdge - 40) break;

        const texKey = ENEMY_TEXTURES[entry.type];
        if (this.textures.exists(texKey)) {
          const sprite = this.add.image(sx + SPRITE_SIZE / 2, y + rowH / 2, texKey)
            .setDisplaySize(SPRITE_SIZE, SPRITE_SIZE);

          const gradeMod = GRADE_MODIFIERS[entry.grade];
          if (gradeMod.tint) sprite.setTint(gradeMod.tint);
          this.tableContainer.add(sprite);

          // Grade badge
          if (entry.grade !== EnemyGrade.NORMAL) {
            const badge = this.add.circle(sx + SPRITE_SIZE - 2, y + 4, 6, GRADE_COLORS[entry.grade], 0.9);
            const badgeLbl = this.add.text(sx + SPRITE_SIZE - 2, y + 4, GRADE_LABELS[entry.grade], {
              fontSize: '7px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
            }).setOrigin(0.5);
            this.tableContainer.add([badge, badgeLbl]);
          }
        }

        const countText = this.add.text(sx + SPRITE_SIZE + 3, y + rowH / 2, `\u00D7${entry.count}`, {
          fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
        }).setOrigin(0, 0.5);
        this.tableContainer.add(countText);

        sx += SPRITE_SIZE + countText.width + 10;
      }

      // Power value
      const pwrText = this.add.text(rightEdge, y + rowH / 2, `${actualPwr}`, {
        fontSize: '11px', color: '#ff88ff', fontFamily: 'monospace',
      }).setOrigin(1, 0.5);
      this.tableContainer.add(pwrText);

      // Separator
      const sep = this.add.graphics();
      sep.lineStyle(0.5, 0x333355, 0.3);
      sep.lineBetween(x, y + rowH, rightEdge, y + rowH);
      this.tableContainer.add(sep);

      y += rowH;
    }

    // Track total content height for scroll bounds
    this.tableContentH = y - (Q4.y + PANEL_TITLE_H);

    // Total power (pinned at bottom, outside mask)
    const totalActual = waves.reduce((s, w) => s + wavePowerActual(w), 0);
    this.totalLabel.setText(`TOTAL: ${Math.round(totalActual)} / ${mapPower(m)} target`);
  }

  // ═══════════════════════════════════════════════════════
  // BUTTONS — centered at bottom
  // ═══════════════════════════════════════════════════════

  private buildButtons(): void {
    const y = CANVAS_HEIGHT - 28;

    createTechButton(this, CANVAS_WIDTH / 2 - 120, y, 'RESET', {
      onClick: () => showConfirmDialog(this, 'Reset power curve to defaults?', () => {
        resetCurve(); this.scene.restart();
      }),
      width: 80, height: 26, fontSize: '10px', color: TECH.DANGER,
    });

    createTechButton(this, CANVAS_WIDTH / 2, y, 'SAVE', {
      onClick: () => showConfirmDialog(this, 'Save current power curve?', () => {
        saveCurve(); SoundManager.play('clickConfirm');
      }, { confirmColor: TECH.SUCCESS }),
      width: 80, height: 26, fontSize: '10px', color: TECH.SUCCESS,
    });

    createTechButton(this, CANVAS_WIDTH / 2 + 120, y, 'REROLL', {
      onClick: () => showConfirmDialog(this, 'Reroll wave compositions?', () => {
        this.cacheKey = ''; this.rebuildTable();
      }, { confirmColor: TECH.ACCENT }),
      width: 80, height: 26, fontSize: '10px', color: TECH.ACCENT,
    });
  }
}
